import React, { useState, createContext, useContext, useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';

import { trpc, createTRPCClient } from './trpc';

// Create context for tRPC client
const TRPCClientContext = createContext<ReturnType<typeof createTRPCClient> | null>(null);

export function useTRPCClient() {
  const client = useContext(TRPCClientContext);
  if (!client) {
    throw new Error('useTRPCClient must be used within TRPCProvider');
  }
  return client;
}

// ============================================================================
// Query Cache Persistence
// Persists specific queries to localStorage for instant loads after page reload
// ============================================================================

const CACHE_KEY = 'tgd-query-cache';
const CACHE_VERSION = 1;
const CACHE_MAX_AGE = 30 * 60 * 1000; // 30 minutes

// Only persist these query keys (to avoid bloating localStorage)
const PERSISTABLE_QUERIES = [
  'funnels.getById',
  'plugins.getAvailableInstancesForStepType',
  'plugins.getInstanceScreenshotsForStore',
  'funnels.getAvailableConditions',
];

interface PersistedCache {
  version: number;
  timestamp: number;
  queries: Array<{
    queryKey: readonly unknown[];
    state: {
      data: unknown;
      dataUpdatedAt: number;
    };
  }>;
}

function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  // tRPC query keys are arrays like [['funnels', 'getById'], { input: {...} }]
  const keyPath = Array.isArray(queryKey[0]) ? queryKey[0].join('.') : '';
  return PERSISTABLE_QUERIES.some(pattern => keyPath.includes(pattern));
}

function saveCache(queryClient: QueryClient) {
  try {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll()
      .filter(query => {
        // Only persist successful queries with data
        if (query.state.status !== 'success' || !query.state.data) return false;
        return shouldPersistQuery(query.queryKey);
      })
      .map(query => ({
        queryKey: query.queryKey,
        state: {
          data: query.state.data,
          dataUpdatedAt: query.state.dataUpdatedAt,
        },
      }));

    if (queries.length === 0) return;

    const persisted: PersistedCache = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      queries,
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(persisted));
  } catch {
    // localStorage might be full or disabled - silently fail
  }
}

function restoreCache(queryClient: QueryClient) {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;

    const persisted: PersistedCache = JSON.parse(raw);
    
    // Check version and age
    if (persisted.version !== CACHE_VERSION) {
      localStorage.removeItem(CACHE_KEY);
      return;
    }
    
    if (Date.now() - persisted.timestamp > CACHE_MAX_AGE) {
      localStorage.removeItem(CACHE_KEY);
      return;
    }

    // Restore queries
    for (const query of persisted.queries) {
      queryClient.setQueryData(query.queryKey, query.state.data, {
        updatedAt: query.state.dataUpdatedAt,
      });
    }
  } catch {
    // Invalid cache - remove it
    localStorage.removeItem(CACHE_KEY);
  }
}

// ============================================================================

interface TRPCProviderProps {
  children: React.ReactNode;
}

export function TRPCProvider({ children }: TRPCProviderProps) {
  const { getToken } = useAuth();
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [queryClient] = useState(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 5 * 60 * 1000,
          gcTime: 30 * 60 * 1000,
          retry: (failureCount, error) => {
            const status = (error as any)?.data?.httpStatus ?? (error as any)?.status;
            if (status === 401 || status === 403) return false;
            return failureCount < 1;
          },
        },
        mutations: {
          // Don't retry on auth errors: the tRPC client already refreshes-and-
          // retries once on a 401 (see createTRPCClient in ./trpc.ts), so a
          // surfaced 401/403 is terminal — retrying here only doubles the load.
          retry: (failureCount, error) => {
            const status = (error as any)?.data?.httpStatus ?? (error as any)?.status;
            if (status === 401 || status === 403) return false;
            return failureCount < 1;
          },
        },
      },
    });

    // Restore cache from localStorage on creation
    restoreCache(client);

    return client;
  });

  // Save cache to localStorage when PERSISTABLE queries change (debounced).
  // Previously every cache event scheduled a save — the 15s analytics poll
  // and 3s screenshot polls churned the cache continuously, and each save
  // re-stringified the multi-100KB persistable payloads even though none of
  // them had changed (audit FE-13). Now only events for persistable query
  // keys schedule a save; a tab-hide flush covers anything in flight.
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const key = (event as { query?: { queryKey?: readonly unknown[] } })?.query?.queryKey;
      if (!key || !shouldPersistQuery(key)) return;
      // Debounce saves to avoid too many writes
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveCache(queryClient);
      }, 1000);
    });

    // Save on page unload / tab hide (mobile Safari never fires beforeunload)
    const flush = () => saveCache(queryClient);
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      unsubscribe();
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [queryClient]);

  const trpcClient = React.useMemo(() => createTRPCClient(getToken), [getToken]);

  return (
    <TRPCClientContext.Provider value={trpcClient}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </trpc.Provider>
    </TRPCClientContext.Provider>
  );
}
